import type { AppearanceModule, AppearanceStyleSet } from '@setplex/tria-api'
import type { Store } from 'effector'
import type { VariantOf } from 'variant'
import {
  attach,
  createStore,
  sample,
  combine,
  createEvent,
  split,
  restore,
} from 'effector'
import { variant, nil, payload, isType, match, constant } from 'variant'
import { api } from '@setplex/tria-api'
import { features, options } from '~/core/flags'
import { formatAppTheme, getModulesFallback } from './lib'

export const init = createEvent()

type Appearance = VariantOf<typeof Appearance>
const Appearance = variant({
  Empty: nil, // initial state, not ready yet
  Done: payload<AppearanceStyleSet>(), // successfully loaded
  Fail: nil, // failed to load
  Fall: nil, // fallback to default in case of customization disabled
})

const getAppearanceFx = attach({
  effect: api.appearance.getAppearanceFx,
})

const fallbackAppearance = createEvent()

const $appearance = createStore<Appearance>(Appearance.Empty())
  .on(getAppearanceFx.done, (_, { result }) => Appearance.Done(result))
  .on(getAppearanceFx.fail, () => Appearance.Fail())
  .on(fallbackAppearance, () => Appearance.Fall())
  .reset(init, api.session.signOutFx.finally, api.events.http.unauthorized)

export const $appTheme = createStore<React.CSSProperties | null>(null) //
  .reset(api.session.signOutFx.finally, api.events.http.unauthorized)

export const $modules: Store<AppearanceModule[] | null> = combine(
  $appearance,
  options.$optionsFlags, // TODO better use modules.$visibility from '~/shared/config'
  (appearance, flags) => {
    const modules = match(appearance, {
      Empty: constant(null),
      Done: ({ payload }) => payload.modules,
      Fail: constant(undefined),
      Fall: constant(undefined),
    })
    if (modules === null) return null // not ready yet
    return modules?.length ? modules : getModulesFallback(flags)
  }
)

export const setIdentifier = createEvent<string>()
export const $currentIdentifier = restore<string | null>(setIdentifier, null)

// ready after modules are known, from either loaded appearance or fallback
export const $ready = $modules.map(Boolean)

// get or fallback appearance on init
split({
  clock: init,
  source: features.$featuredFlags,
  match: (features) =>
    features.appearanceCustomizationEnabled ? 'get' : 'fallback',
  cases: {
    get: getAppearanceFx,
    fallback: fallbackAppearance,
  },
})

// fill theme store if appearance is loaded
sample({
  clock: $appearance,
  filter: (appearance: Appearance) => isType(appearance, Appearance.Done),
  fn: (appearance) => formatAppTheme(appearance.payload),
  target: $appTheme,
})
